import React, { useState, useEffect } from 'react';
import { WCContainer } from '../../types/wordpress';
import { WordPressAPI } from '../../services/api/api';
import ContainerForm from './ContainerForm';
import { Button, Table, Modal, message, Popconfirm, Tag, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, UndoOutlined, SearchOutlined } from '@ant-design/icons';

const ContainerList: React.FC = () => {
    const [containers, setContainers] = useState<WCContainer[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingContainer, setEditingContainer] = useState<WCContainer | null>(null);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchContainers();
    }, []);

    const fetchContainers = async () => {
        try {
            const data = await WordPressAPI.getContainers();
            setContainers(data);
        } catch (error) {
            message.error('Failed to fetch containers');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredContainers = () => {
        return containers.filter(container =>
            container.title.rendered.toLowerCase().includes(searchText.toLowerCase()) ||
            container.common_sizes?.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    const handleSoftDelete = async (id: number) => {
        try {
            await WordPressAPI.updateContainer(id, { is_deleted: true });
            message.success('Container deactivated successfully');
            fetchContainers();
        } catch (error) {
            message.error('Failed to deactivate container');
            console.error(error);
        }
    };

    const handleRestore = async (id: number) => {
        try {
            await WordPressAPI.updateContainer(id, { is_deleted: false });
            message.success('Container restored successfully');
            fetchContainers();
        } catch (error) {
            message.error('Failed to restore container');
            console.error(error);
        }
    };

    const handleEdit = (container: WCContainer) => {
        setEditingContainer(container);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingContainer(null);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingContainer(null);
    };

    const handleFormSubmit = async (values: Partial<WCContainer>) => {
        try {
            if (editingContainer) {
                await WordPressAPI.updateContainer(editingContainer.id, values);
                message.success('Container updated successfully');
            } else {
                await WordPressAPI.createContainer(values);
                message.success('Container created successfully');
            }
            setIsModalVisible(false);
            fetchContainers();
        } catch (error) {
            message.error('Failed to save container');
            console.error(error);
        }
    };

    const columns: ColumnsType<WCContainer> = [
        {
            title: 'Name',
            dataIndex: 'title',
            render: (title) => title.rendered,
            key: 'title',
            sorter: (a, b) => a.title.rendered.localeCompare(b.title.rendered),
        },
        {
            title: 'Common Sizes',
            dataIndex: 'common_sizes',
            key: 'common_sizes',
            sorter: (a, b) => (a.common_sizes || '').localeCompare(b.common_sizes || ''),
        },
        {
            title: 'External Dimensions (L×W×H)',
            key: 'external_dimensions',
            render: (_, record) => (
                `${record.external_dimensions_length || '-'} × ${record.external_dimensions_width || '-'} × ${record.external_dimensions_height || '-'}`
            ),
        },
        {
            title: 'Internal Dimensions (L×W×H)',
            key: 'internal_dimensions',
            render: (_, record) => (
                `${record.internal_dimensions_length || '-'} × ${record.internal_dimensions_width || '-'} × ${record.internal_dimensions_height || '-'}`
            ),
        },
        {
            title: 'Status',
            key: 'is_deleted',
            filters: [
                { text: 'Active', value: 'active' },
                { text: 'Inactive', value: 'inactive' },
            ],
            onFilter: (value, record) => {
                if (value === 'active') {
                    return !record.is_deleted;
                }
                return record.is_deleted;
            },
            render: (_, record) => (
                record.is_deleted ?
                    <Tag color="red">Inactive</Tag> :
                    <Tag color="green">Active</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="flex flex-col sm:flex-row gap-2">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        className="whitespace-nowrap"
                    >
                        Edit
                    </Button>
                    {record.is_deleted ? (
                        <Popconfirm
                            title="Restore this container?"
                            onConfirm={() => handleRestore(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="primary"
                                ghost
                                icon={<UndoOutlined />}
                                className="whitespace-nowrap"
                            >
                                Restore
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm
                            title="Deactivate this container?"
                            onConfirm={() => handleSoftDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                className="whitespace-nowrap"
                            >
                                Deactivate
                            </Button>
                        </Popconfirm>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full p-4">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">Containers</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                        placeholder="Search containers..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-full sm:w-[300px]"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                        className="whitespace-nowrap"
                    >
                        Add New Container
                    </Button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table
                    columns={columns}
                    dataSource={getFilteredContainers()}
                    loading={loading}
                    rowKey="id"
                    rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        className: 'responsive-pagination'
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <Modal
                title={editingContainer ? 'Edit Container' : 'Add New Container'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                className="responsive-modal"
                destroyOnClose={true}
            >
                <ContainerForm
                    key={editingContainer?.id || 'new'}
                    initialValues={editingContainer}
                    onSubmit={handleFormSubmit}
                    onCancel={handleModalClose}
                />
            </Modal>
        </div>
    );
};

export default ContainerList; 